import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// Footer Image interface
export interface FooterImage {
  id: string;
  description: string;
  imageAddress: string;
  imageUrl: string;
  expiryDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface FooterImagePayload extends Partial<FooterImage> {
  description: string;
  imageAddress: string;
  imageUrl: string;
  expiryDate?: string;
}

interface GetAllFooterImagesParams {
  skip?: number;
  limit?: number;
  search?: string;
  sort?: string;
  filters?: string;
}

class FooterImageService {
  static ENDPOINT = 'api/v2/footer-image';

  static async getFooterImage(id: string): Promise<APIResponseType> {
    if (!id) throw new Error('Footer Image ID is required to fetch details');
    try {
      const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getAllFooterImages(params?: GetAllFooterImagesParams): Promise<APIResponseType> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filters) queryParams.append('filters', params.filters);
      }
      const queryString = queryParams.toString();
      const url = queryString ? `${this.ENDPOINT}?${queryString}` : this.ENDPOINT;
      const response = await apiService.get<APIResponseType>(url);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async addFooterImage(payload: FooterImagePayload): Promise<APIResponseType> {
    try {
      const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteFooterImage(id: string): Promise<APIResponseType> {
    if (!id) throw new Error('Footer Image ID is required for deletion');
    try {
      const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${id}`);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async editFooterImage(id: string, payload: Partial<FooterImagePayload>): Promise<APIResponseType> {
    if (!id) throw new Error('Footer Image ID is required for editing');
    if (!Object.keys(payload).length) throw new Error('Edit payload cannot be empty');
    try {
      const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${id}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default FooterImageService; 